import { Outfit, OutfitBlock, PlayerCharacter } from "../../../model/character";

import { Box, Button, Chip, Divider, FormGroup, Stack, styled, TextField, Typography } from "@mui/material";

import { useEffect, useState } from "react";
import { v4 } from "uuid";
import { NumberField } from "../../../components/formFields/NumberField";
import { TabBar } from "../../../components/TabBar";
import AbilityEditor from "../AbilityEditor";
import { Modal, ModalAction, ModalContentSection } from "../../../components/Modal";
import FormFieldLabel from "../../../components/formFields/FormFieldLabel";
import SelectField from "../../../components/formFields/SelectField";
import LibraryModal from "../libraryModal";
import RemoveIcon from '@mui/icons-material/Remove';
import { v4 as uuidv4 } from 'uuid';
import TagList from "../../../components/TagList";
import { ButtonFlatIcon } from "../../../components/ButtonFlat";

const defaultBlock: OutfitBlock = {
    id: "",
    name: "Outfit",
    slots: 0,
    armor: 0,
    armorAtZero: 0,
    defence: 0,
    endurance: 0,
    outfitType: [],
    addFeatures: [],
    removeFeatures: [],
    passives: [],
    strongHits: [],
    resources: 0,
    knowledge: 0,
    influence: 0,
    acquire: 0,
    notes: "",
    size: "standard"
}

const defaultOutfit: Outfit = {
    base: { ...defaultBlock },
    variations: [],
    modifications: [],
    id: "",
    description: "",
    carried: "none",
    name: "New Outfit",
    isStashed: false,
}

const BlockList = styled('div')({
    display: "flex",
    height: "33px"
})

const BlockListLabel = styled('div')({
    flexGrow: 1,
    paddingLeft: "8px",
    display: "flex",
})

const BlockListButton = styled('button')(({ theme }) => ({
    background: "unset",
    border: 0,
    outline: 0,
    cursor: "pointer",
    padding: 0,
    display: "inline",
    color: theme.palette.text.primary,
    fontSize: theme.typography.body1.fontSize,
    
    "&:hover": {
        textDecoration: "underline"
    }
}));

export const GetNewOutfit = (): Outfit => {
    return {
        ...defaultOutfit,
        id: v4()
    }
}

const OutfitBlockEditor = (props: {outfitBlock: OutfitBlock, setOutfitBlock: (outfitBlock: OutfitBlock) => void}) => {
    const { outfitBlock, setOutfitBlock } = props;

    const onChange = (key: string, value: any) => {
        setOutfitBlock({
            ...outfitBlock,
            [key]: value
        })
    }

    const handleAddFeature = (item: string) => {
        const list = [ ...outfitBlock.addFeatures, item ];
        onChange("addFeatures", list);
    }

    const handleRemoveFeature = (index: number) => {
        const features = [ ...outfitBlock.addFeatures ];
        features.splice(index, 1);
        onChange("addFeatures", features);
    }

    const handleAddType = (item: string) => {
        const list = [ ...outfitBlock.outfitType, item ];
        onChange("outfitType", list);
    }

    const handleRemoveType = (index: number) => {
        const current = [ ...outfitBlock.outfitType ];
        current.splice(index, 1);
        onChange("outfitType", current);
    }

    return <>
        <Stack spacing={1}>
            <FormFieldLabel label="Name">
                <TextField fullWidth type="text" variant="standard" value={outfitBlock.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
            </FormFieldLabel>
            <SelectField value={outfitBlock.size} onChange={(value) => onChange("size", value)} options={[
                { name: "Size - Trinket (0.25)", value: "trinket" },
                { name: "Size - Small (1)", value: "small" } ,
                { name: "Size - Standard (2)", value: "standard" } ,
                { name: "Size - Large (3)", value: "large" } ,
            ]} />
            <FormFieldLabel label="Equipment Slots" justifyContent="end">
                <NumberField step={1} value={outfitBlock.slots} onChange={(value) => onChange("slots", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Armour" justifyContent="end">
                <NumberField step={1} value={outfitBlock.armor} onChange={(value) => onChange("armor", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Armour at 0 Endurance" justifyContent="end">
                <NumberField step={1} value={outfitBlock.armorAtZero} onChange={(value) => onChange("armorAtZero", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Defence" justifyContent="end">
                <NumberField step={1} value={outfitBlock.defence} onChange={(value) => onChange("defence", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Endurance" justifyContent="end">
                <NumberField step={1} value={outfitBlock.endurance} onChange={(value) => onChange("endurance", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Acquire" justifyContent="end">
                <NumberField step={1} value={outfitBlock.acquire} onChange={(value) => onChange("acquire", value)} customRenderer={(v) => `${v}t`} />
            </FormFieldLabel>
            <FormFieldLabel label="Influence" justifyContent="end">
                <NumberField step={1} value={outfitBlock.influence} onChange={(value) => onChange("influence", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Resources" justifyContent="end">
                <NumberField step={1} value={outfitBlock.resources} onChange={(value) => onChange("resources", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Knowledge" justifyContent="end">
                <NumberField step={1} value={outfitBlock.knowledge} onChange={(value) => onChange("knowledge", value)}  customRenderer={(v) => `${v}kn`} />
            </FormFieldLabel>
        </Stack>
        <ModalContentSection title="Weapon Type">
            <TagList items={outfitBlock.outfitType} onAdd={handleAddType} onRemove={handleRemoveType} />
        </ModalContentSection>
        <ModalContentSection title="Keywords">
            <TagList items={outfitBlock.addFeatures} onAdd={handleAddFeature} onRemove={handleRemoveFeature} />
        </ModalContentSection>
        <AbilityEditor title="Passive Abilities" abilities={outfitBlock.passives} setAbilities={(value) => onChange("passives", value)} />
        <Box paddingLeft={1}>
            <Typography>Notes</Typography>
            <TextField value={outfitBlock.notes} onChange={(event) => onChange('notes', event.currentTarget.value)} multiline fullWidth />
        </Box>
        <AbilityEditor title="Strong Hit Options" abilities={outfitBlock.strongHits} setAbilities={(value) => onChange("strongHits", value)} />
    </>
}

export const OutfitEditor = (props: {
    setOutfit: (value: Outfit) => void,
    onClose: () => void,
    outfit?: Outfit;
    character: PlayerCharacter,
}) => {
    const { outfit, character, setOutfit, onClose } = props;
    const [ current, setCurrent ] = useState<Outfit>(defaultOutfit);
    const [ selectVariation, setSelectVariation ] = useState(false);
    const [ selectModification, setSelectModification ] = useState(false);
    const [ currentVariation, setCurrentVariation ] = useState<number | undefined>(undefined);
    const [ currentModification, setCurrentModification ] = useState<number | undefined>(undefined);

    useEffect(() => {
        if(outfit !== undefined)
            setCurrent(outfit ?? defaultOutfit);
    }, [outfit]);


    const handleModalCancel = () => {
        onClose();
    }

    const handleModalSubmit = () => {
        setOutfit(current)
    }

    const onChange = (key: string, value: any) => {
        setCurrent((c) => ({
            ...c,
            [key]: value
        }))
    }

    const handleSelectVariation = (block: OutfitBlock) => {
        console.dir(block);
        const variations = [ ...current.variations, { ...block, id: uuidv4() } ]
        setCurrent((c) => ({
            ...c,
            variations
        }));
    }

    const handleRemoveVariation = (index: number) => {
        const variations = [ ...current.variations ];
        variations.splice(index,1);
        setCurrent((c) => ({
            ...c,
            variations
        }));
    }

    const handleChangeVariation = (index: number, variation: OutfitBlock) => {
        const variations = [ ...current.variations ];
        variations[index] = variation;
        setCurrent((c) => ({
            ...c,
            variations
        }));
    }

    const handleSelectModification = (block: OutfitBlock) => {
        const modifications = [ ...current.modifications, { ...block, id: uuidv4() } ]
        setCurrent((c) => ({
            ...c,
            modifications
        }));
    }

    const handleRemoveModification = (index: number) => {
        const modifications = [ ...current.modifications ];
        modifications.splice(index,1);
        setCurrent((c) => ({
            ...c,
            modifications
        }));
    }

    const handleChangeModification = (index: number, modification: OutfitBlock) => {
        const modifications = [ ...current.modifications ];
        modifications[index] = modification;
        setCurrent((c) => ({
            ...c,
            modifications
        }));
    }

    const actionList: ModalAction[] = [];
        if(currentModification !== undefined || currentVariation !== undefined) {
            actionList.push({
                label: "Return",
                onClick: () => { setCurrentModification(undefined); setCurrentVariation(undefined); },
                size: 12
            })
        }
        actionList.push({
            label: "Cancel",    
            onClick: handleModalCancel,
        });
        actionList.push({
            label: "Save",    
            onClick: handleModalSubmit,
            primary: true,
        });
    
    return <>
        <Modal
            title={current.name}
            open={outfit !== undefined}
            onClose={handleModalCancel}
            actions={actionList}
        >
            <LibraryModal defaultItem={defaultBlock} onClose={() => setSelectVariation(false)} show={selectVariation} onSelect={handleSelectVariation} prefix="lov" title="Select Outfit Variation" />
            <LibraryModal defaultItem={defaultBlock} onClose={() => setSelectModification(false)} show={selectModification} onSelect={handleSelectModification} prefix="lom" title="Select Outfit Modification" />
            <FormGroup>
                <FormFieldLabel label="Name">
                    <TextField type="text" variant="standard" value={current.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
                </FormFieldLabel>
                <ModalContentSection title="Variation" onAdd={() => setSelectVariation(true)} collapsable>
                    {current.variations.map((variation, index) => {
                        return <BlockList>
                            <BlockListLabel><BlockListButton onClick={() => { setCurrentVariation(index); setCurrentModification(undefined); }}>{variation.name}</BlockListButton></BlockListLabel>
                            <ButtonFlatIcon onClick={() => handleRemoveVariation(index)}><RemoveIcon /></ButtonFlatIcon>
                        </BlockList>
                    })}
                </ModalContentSection>
                <ModalContentSection title="Modifications" onAdd={() => setSelectModification(true)} collapsable>
                    {current.modifications.map((modification, index) => {
                        return <BlockList>
                            <BlockListLabel><BlockListButton onClick={() => { setCurrentModification(index); setCurrentVariation(undefined); }}>{modification.name}</BlockListButton></BlockListLabel>
                            <ButtonFlatIcon onClick={() => handleRemoveModification(index)}><RemoveIcon /></ButtonFlatIcon>
                        </BlockList>
                    })}
                </ModalContentSection>
                <Divider sx={{ marginY: 2}}/>

                {currentVariation !== undefined && <OutfitBlockEditor outfitBlock={current.variations[currentVariation]} setOutfitBlock={(wb) => handleChangeVariation(currentVariation, wb)} />}
                {currentModification !== undefined && <OutfitBlockEditor outfitBlock={current.modifications[currentModification]} setOutfitBlock={(wb) => handleChangeModification(currentModification, wb)} />}
                {currentModification === undefined && currentVariation === undefined && <OutfitBlockEditor outfitBlock={current.base} setOutfitBlock={(wb) => onChange("base", wb)} />}

                <Box paddingLeft={1}>
                    <Typography>Description</Typography>
                    <TextField value={current.description} onChange={(event) => onChange('description', event.currentTarget.value)} multiline fullWidth />
                </Box>
                {/* <TabBar pages={[
                    { label: "Base", content: <OutfitBlockEditor outfitBlock={current.base} setOutfitBlock={(wb) => onChange("base", wb)} /> },
                    { label: "Variations", content: <>
                        <LibraryModal defaultItem={defaultBlock} onClose={() => setSelectVariation(false)} show={selectVariation} onSelect={handleSelectVariation} prefix="lov" title="Select Outfit Variation" />
                        {current.variations.map((variation, index) => {
                            return <div key={index}>
                                <OutfitBlockEditor outfitBlock={variation} setOutfitBlock={(wb) => handleChangeVariation(index, wb)} />
                                <Button onClick={() => handleRemoveVariation(index)}>Remove {variation.name}</Button>
                            </div>
                        })}
                        <Button onClick={() => setSelectVariation(true)}>Add Variation</Button>
                    </> },
                    { label: "Mods", content: <>
                        <LibraryModal defaultItem={defaultBlock} onClose={() => setSelectModification(false)} show={selectModification} onSelect={handleSelectModification} prefix="lom" title="Select Outfit Modification" />
                        {current.modifications.map((modification, index) => {
                            return <div key={index}>
                                <OutfitBlockEditor outfitBlock={modification} setOutfitBlock={(wb) => handleChangeModification(index, wb)} />
                                <Button onClick={() => handleRemoveModification(index)}>Remove {modification.name}</Button>
                            </div>
                        })}
                        <Button onClick={() => setSelectModification(true)}>Add Modification</Button>
                    </> },
                ]} /> */}
            </FormGroup>
        </Modal>
    </>
}